import { MecordColors } from "Constants/Color";
import React from "react";
import { showCustomSwal } from "helpers/swalHelper";
import Swal from "sweetalert2";

interface ExportButtonProps {
  label?: string; // Button label text (defaults to "Export")
  icon?: string; // Optional icon class (defaults to "bi bi-box-arrow-up")
  onExport: () => Promise<{ blob: any; filename: string }>; // Export function that returns blob and filename
  className?: string; // Additional CSS classes
  disabled?: boolean; // Disabled state
  style?: React.CSSProperties; // Additional inline styles
}

const ExportButton: React.FC<ExportButtonProps> = ({
  label = "Export",
  icon = "bi bi-box-arrow-up",
  onExport,
  className = "",
  disabled = false,
  style,
}) => {
  const themeColor = MecordColors?.ThemeBGColor;

  const baseClasses = `btn custom-export-button ${className}`.trim();
  const iconClasses = icon ? `${icon} align-baseline me-1` : "";
  const combinedClasses = icon ? `hstack gap-2 ${baseClasses}`.trim() : baseClasses;

  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#ffffff',
    borderColor: themeColor,
    color: themeColor,
    borderRadius: '4px',
    padding: '4px 12px',
    height: '32px',
    minHeight: '32px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    opacity: disabled ? 0.6 : 1,
    border: `1px solid ${themeColor}`,
    ...style,
  };

  const handleExport = async () => {
    if (disabled) return;

    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await onExport();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export data";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const buttonContent = (
    <>
      {icon && <i className={iconClasses}></i>}
      {label}
    </>
  );

  return (
    <>
      <style>
        {`
          .custom-export-button {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            border-radius: 4px !important;
            padding: 4px 12px !important;
            height: 32px !important;
            min-height: 32px !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            transition: all 0.2s ease !important;
            border: 1px solid ${themeColor} !important;
          }
          .custom-export-button:hover:not(:disabled) {
            // background-color: ${themeColor} !important;
            border-color: ${themeColor} !important;
            // color: #fff !important;
            opacity: 1 !important;
          }
          .custom-export-button:focus {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            box-shadow: 0 0 0 0.2rem ${themeColor}40 !important;
          }
          .custom-export-button:disabled {
            background-color: #ffffff !important;
            border-color: ${themeColor} !important;
            color: ${themeColor} !important;
            opacity: 0.6 !important;
            cursor: not-allowed !important;
          }
        `}
      </style>
      <button
        type="button"
        className={combinedClasses}
        disabled={disabled}
        style={buttonStyle}
        onClick={handleExport}
      >
        {buttonContent}
      </button>
    </>
  );
};

export default ExportButton;
